import React, { useEffect } from 'react';
import moment from 'moment';
import styles from './styles.module.scss';
import { useStore } from '../../../../../stores/root';

// assets
import { faXmarkCircle } from '@fortawesome/pro-light-svg-icons';

// services
import ErrorService from '../../../../../services/general/error';
import OrderService from '../../../../../services/orders/order';
import OrderPriceUpdateService from '../../../../../services/orders/priceUpdates';

// props
import { IPaginationResourceShort } from '../../../../../props/general';
import { IIncomeInvoiceResourceShortProps } from '../../../../../props/finance/incomes/invoices/invoice';
import { IOrderPriceUpdateResourceShortProps } from '../../../../../props/orders/priceUpdates';

// components
import { ActionButton, ContextualMenuItemType, IContextualMenuItem, Link, Stack } from '@fluentui/react';
import Text from './../../../../typography/text';
import Tag from '../../../../uiframeworks/tag';
import ListItems from '../../../../uiframeworks/listitems';
import { ListItemsColumnProps, ListItemsFilterByProps, ListItemsOrderByProps, ListItemsQuickFilterProps } from '../../../../uiframeworks/listitems/props';
import OrderPriceUpdateStatus from '../../../../../manifests/status/orderPriceUpdateStatus';
import GeneralService from '../../../../../services/general';
import PermissionsService from '../../../../../services/permissions';
import RequestOrderPriceUpdateDetails from '../details';
import ApproveOrderPriceUpdateForm from '../form/approve';
import RejectOrderPriceUpdateForm from '../form/reject';

type OrderPriceUpdateListProps = {
    qs?: string[];
    columns?: TOrderPriceUpdateListCoumn[];
    requests?: IOrderPriceUpdateResourceShortProps[];
    quickFilters?: ListItemsQuickFilterProps[];
    hideSearch?: boolean;
    hideCommandBar?: boolean;
    hideCreateButton?: boolean;
    variant?: 'card' | 'plain';
}

export type TOrderPriceUpdateListCoumn = 'history' | 'status' | 'notes' | 'originalPrice' | 'newPrice' | 'container' | 'goods' | 'truck' | 'order' | 'action';

const OrderPriceUpdateList: React.FC<OrderPriceUpdateListProps> = (props: OrderPriceUpdateListProps) => {
    const shownColumns: TOrderPriceUpdateListCoumn[] = props.columns || [
        'history',
        'status',
        'newPrice',
        'notes',
        'action'
    ];

    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [requests, setRequests] = React.useState<IOrderPriceUpdateResourceShortProps[]>(props.requests || []);
    const [activeRequest, setActiveRequest] = React.useState<IOrderPriceUpdateResourceShortProps | undefined>();
    const [pagination, setPagination] = React.useState<IPaginationResourceShort | undefined>();
    const [keyword, setKeyword] = React.useState<string>("");
    const [activeSurface, setActiveSurface] = React.useState<string | undefined>();
    const [filterBy, setFilterBy] = React.useState<ListItemsFilterByProps[] | undefined>();

    const [orderBy, setOrderBy] = React.useState<ListItemsOrderByProps | undefined>(undefined);

    const columns: ListItemsColumnProps[] = [
        {
            key: "order",
            name: "Order",
            minWidth: 125,
            maxWidth: 125,
            fieldName: 'id',
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Link href={"/orders/" + item.order.id} target={'_blank'}><Text size={'small'}>#{OrderService.getOrderNumber(item.order.id)}</Text></Link>
                </Stack>
            }
        },
        {
            key: "originalPrice",
            name: "Original Price",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    
                </Stack>
            }
        },
        {
            key: "notes",
            name: "Notes",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>{item.notes || "-"}</Text>
                </Stack>
            }
        },
        {
            key: "action",
            name: "",
            minWidth: 25,
            maxWidth: 25,
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                const menuItems: IContextualMenuItem[] = [];

                const hasApproveAccess = PermissionsService.hasPermission(['orders.prices.update.approve.all'], user.permissions);
                const hasRejectAccess = PermissionsService.hasPermission(['orders.prices.update.reject.all'], user.permissions);
                const isSales = (item.order.sales || []).findIndex((s) => s.sales.id === user.data?.id) > -1;
                
                menuItems.push({
                    key: 'view',
                    text: 'View details',
                    onClick: () => {
                        setActiveRequest(item);
                        setActiveSurface('request.details');
                    }
                });
                
                menuItems.push({
                    key: 'divider',
                    itemType: ContextualMenuItemType.Divider
                });

                // only people with approve all price update and sales can approve
                if ((hasApproveAccess || isSales) && item.status === 'requested') {
                    menuItems.push({
                        key: 'approve',
                        text: 'Approve',
                        onClick: () => {
                            setActiveRequest(item);
                            setActiveSurface('request.approve');
                        }
                    });
                }

                // only people with reject all price update and sales can reject
                if ((hasRejectAccess || isSales) && item.status === 'requested') {
                    menuItems.push({
                        key: 'reject',
                        text: 'Reject',
                        onClick: () => {
                            setActiveRequest(item);
                            setActiveSurface('request.reject');
                        }
                    });
                }
                
                return <Stack.Item styles={{ root: { padding: '4px 0px' } }}>
                    {menuItems.length > 0 ? <Stack.Item className={"detailsListActionRow"}>
                       <ActionButton className={'detailsListActionButton'} menuProps={{items: menuItems}} />
                    </Stack.Item> : null}
                </Stack.Item>;
            }
        },
        {
            key: "newPrice",
            name: "New Price (incl. PPn & PPh)",
            minWidth: 200,
            maxWidth: 200,
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'}>Rp. {GeneralService.getNumberWithSeparator(item.finalPrice)}</Text>
                </Stack>
            }
        },
        {
            key: "history",
            name: "History",
            minWidth: 250,
            onRender: (item: IOrderPriceUpdateResourceShortProps) => {
                return <Stack styles={{ root: { padding: '4px 0px' } }}>
                    <Text size={'small'} style={{fontStyle: 'italic'}}>Requested by {item.requestedBy.name} at {moment(item.requestedAt).format("DD/MM/YYYY")}</Text>
                    {item.status === 'approved' && item.respondedBy ? <Text size={'small'} style={{fontStyle: 'italic'}} className={'color-green'}>Approved by {item.respondedBy.name} at {moment(item.respondedAt).format("DD/MM/YYYY")}</Text> : null}
                    {item.status === 'rejected' && item.respondedBy ? <Text size={'small'} style={{fontStyle: 'italic'}} className={'color-red'}>Rejected by {item.respondedBy.name} at {moment(item.respondedAt).format("DD/MM/YYYY")}. Justification: {item.justification}</Text> : null}
                </Stack>
            }
        },
        {
            key: "status",
            name: "Status",
            minWidth: 75,
            maxWidth: 75,
            onRender: (item: IIncomeInvoiceResourceShortProps) => {
                const status = OrderPriceUpdateStatus.find((s) => s.key === item.status);
                return <Stack styles={{ root: { padding: '2px 0px' } }} horizontal>
                    <Tag size={'small'} text={status?.text || ""} variant={status?.tagVariant} />
                </Stack>
            }
        }
    ];

    useEffect(() => {
        if (!props.requests) {
            _onRetrieveOrderPriceUpdates();
        } else {
            setLoaded(true);
        }
    }, [keyword, orderBy, filterBy]);

    useEffect(() => {
        const orderByQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by'));
        const orderByDirectionQs = (props.qs || []).find((qs) => qs.toLowerCase().startsWith('order_by_direction'));

        if (orderByQs) {
            setOrderBy({
                fieldName: orderByQs.split("=")[1],
                direction: orderByDirectionQs ? orderByDirectionQs.split("=")[1] as any : "asc"
            });
        }
    }, [props.qs]);

    const _onRetrieveOrderPriceUpdates = async (pageNumber?: number) => {
        try {
            setLoaded(false);

            const qs: string[] = props.qs ? JSON.parse(JSON.stringify(props.qs)) : [];
            qs.push(`top=all`);
            if (pageNumber) { qs.push(`page=${pageNumber}`); }
            if (keyword && keyword.trim() !== "") { qs.push(`search=${keyword}`) }
            if (orderBy) {
                const orderByIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by='));
                const orderByDirectionIndex = qs.findIndex((qs) => qs.toLowerCase().startsWith('order_by_direction='));

                if (orderByIndex > -1) {
                    qs.splice(orderByIndex, 1, 'order_by=' + orderBy.fieldName);
                } else {
                    qs.push("order_by=" + orderBy.fieldName);
                }

                if (orderByDirectionIndex > -1) {
                    qs.splice(orderByDirectionIndex, 1, 'order_by_direction=' + orderBy.direction);
                } else {
                    qs.push("order_by_direction=" + orderBy.direction);
                }
            }
            (filterBy || []).forEach((f) => {
                qs.push(`${f.key}=${f.selected.map((sel) => sel.key).join(";")}`);
            });

            const results = await OrderPriceUpdateService.retrieve(qs.join("&"));
            setRequests(results.data);
            setPagination(results.pagination);
            setLoaded(true);
        } catch (e) {
            banner.add({
                key: 'retrieve_order_price_update_list_error',
                text: 'Failed to retrieve order price update list. Error: ' + ErrorService.getMessage(e),
                icon: faXmarkCircle,
                variant: 'error'
            });
        }
    }

    const _onKeywordChanged = (value?: string) => {
        setLoaded(false);
        setKeyword(value || "");
    }

    const _onSurfaceDismissed = (refresh?: boolean) => {
        setActiveSurface(undefined);
        setActiveRequest(undefined);

        if (refresh) { _onRetrieveOrderPriceUpdates() }
    }

    return <Stack className={styles.container} tokens={{ childrenGap: 20 }}>
        <ListItems key={"__sys_listitems_orderPriceUpdates"}
            pagination={pagination}
            shownColumns={props.columns || shownColumns}
            loaded={loaded}
            items={requests}
            columns={columns}
            orderBy={orderBy}
            itemText={'price updates'}
            quickFilters={props.quickFilters}
            filterBy={filterBy}
            properties={{hideSearch: props.hideSearch || props.hideCommandBar}}
            onOrderByChange={setOrderBy}
            onPageChange={_onRetrieveOrderPriceUpdates}
            onSearch={_onKeywordChanged}
            onFilterByChange={(filterBy) => setFilterBy([...filterBy])} />
        {activeSurface === 'request.details' && activeRequest ? <RequestOrderPriceUpdateDetails requestId={activeRequest.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'request.approve' && activeRequest ? <ApproveOrderPriceUpdateForm requestId={activeRequest.id} onDismissed={_onSurfaceDismissed} /> : null}
        {activeSurface === 'request.reject' && activeRequest ? <RejectOrderPriceUpdateForm requestId={activeRequest.id} onDismissed={_onSurfaceDismissed} /> : null}
    </Stack >;
};

export default OrderPriceUpdateList;
